<template>
  <div class="ride-set-location">
    <div class="wrapper">
      <div class="subtitle" @click="$emit('close')">
        <i class="fa fa-chevron-left" />&nbsp;
        <span>{{ $t("ride.set-location-on-map") }}</span>
      </div>
      <div class="field" style="margin-bottom: 0px">
        <p class="control has-icons-left">
          <input
            ref="autocomplete"
            class="input is-medium has-text-info"
            v-model="locationInput"
            :placeholder="placeholderText"
            onfocus="value = ''"
            v-on:keyup="checkLength"
            type="text"
          />
          <span class="icon is-small is-left">
            <i class="fas fa-globe" />
          </span>
        </p>
      </div>
      <div v-if="error" class="error-msg" @click="error = null">
        <span class="tag is-danger"
          >{{ error }}&nbsp;<i class="fa fa-times-circle"
        /></span>
      </div>

      <div class="map-wrapper" id="ride-set-location">
        <div :id="mapID" class="map">
          <div class="leaflet-top leaflet-right">
            <div class="center-btn button is-rounded" @click="recenter">
              <i class="fa fa-crosshairs" :class="{ 'fa-spin': spin }" />
            </div>
          </div>
          <div class="leaflet-top">
            <fade-transition>
              <div v-if="showDropoffNearby" class="dropoff-warning">
                <div class="tag is-white has-text-danger">
                  <i>{{ $t("ride.dropoff-near-current-location") }}</i>
                </div>
              </div>
            </fade-transition>
          </div>
        </div>
      </div>

      <div class="confirm-btn">
        <span
          class="button is-rounded is-medium is-fullwidth"
          :class="isPickup ? 'is-info' : 'is-success'"
          :disabled="disableButton"
          @click="confirmLocation"
          >{{ confirmButtonText }}</span
        >
      </div>
    </div>
  </div>
</template>

<script>
import $Scriptjs from "scriptjs";
import L from "leaflet";
import "leaflet.locatecontrol";
// import "leaflet-edgebuffer";
import "leaflet.awesome-markers";
import geo from "@/geo";

var pickupIcon = { icon: "street-view", markerColor: "blue" };
var pickupDragIcon = { icon: "crosshairs", markerColor: "blue", spin: true };
var dropoffIcon = { icon: "flag-checkered", markerColor: "green" };
var dropoffDragIcon = { icon: "crosshairs", markerColor: "green", spin: true };

export default {
  name: "RideSetLocation",
  props: {
    stopType: String,
  },
  data() {
    return {
      mapID: "ride-set-location-map",
      map: undefined,
      marker: undefined,
      error: null,
      locationInput: null,
      place: undefined, // google place result
      addressName: "",
      fullAddress: "",
      streetName: "",
      googleStreetName: "",
      moved: false,
      spin: false,
      showDropoffNearby: false,
    };
  },
  computed: {
    isPickup() {
      return this.stopType === "pickup";
    },
    placeholderText() {
      if (this.isPickup) return this.$t("passenger.current-location");
      return this.$t("passenger.enter-dropoff-location");
    },
    confirmButtonText() {
      if (this.isPickup) return this.$t("transport.confirm-pickup");
      else return this.$t("transport.confirm-dropoff");
    },
    disableButton() {
      if (this.isPickup) return false;
      return !this.moved; // otherwise dropoff, disable true if dropoff not moved
    },
  },
  mounted() {
    var url = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBYOwhqakcxLQQLpljhkug2WsNSA02cqss&libraries=places`;
    $Scriptjs(url, () => this.initGoogle());
    this.initMap();
  },
  methods: {
    checkLength() {
      var errMsg = `Select address from list or drag map to set pin`;
      if (this.locationInput && this.locationInput.length > 15)
        this.error = errMsg;
      else this.error = null;
    },
    initGoogle() {
      var opts = { types: [], componentRestrictions: { country: "th" } };
      var el = this.$refs.autocomplete;
      var autocomplete = new google.maps.places.Autocomplete(el, opts);
      this.geolocate(autocomplete);

      autocomplete.addListener("place_changed", () => {
        this.error = "";
        var place = autocomplete.getPlace();
        this.addressName = place.name;
        this.fullAddress = place.formatted_address;
        this.url = place.url;
        this.place = place;
        if (place.address_components) {
          if (place.address_components[0]) {
            this.googleStreetName = place.address_components[0].long_name;
          }
        }

        this.locationInput = place.name;
        var lat = place.geometry.location.lat();
        var lng = place.geometry.location.lng();
        this.marker.closePopup();
        this.updateMarkerLocation([lat, lng]);
        this.moved = true;
      });
    },
    // Bias autocomplete to the user geolocation,
    geolocate(autocomplete) {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition((position) => {
        var coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        var circle = new google.maps.Circle({
          center: coords,
          radius: position.coords.accuracy,
        });
        autocomplete.setBounds(circle.getBounds());
      });
    },
    recenter() {
      var coords = this.$store.getters.getUserLocation;
      this.updateMarkerLocation(coords, true);
      this.spin = true;
      setTimeout(() => (this.spin = false), 600);
    },
    updateMarkerLocation(coords, flyTo) {
      this.marker.setLatLng(coords);
      if (flyTo) this.map.flyTo(coords);
      else this.map.setView(coords, 17);
    },
    initMap() {
      var tilesURL = this.$t("geo.map-tiles"); // language specific maps
      var bangkokCoords = [13.73114, 100.57529]; // soi 41
      var initialLocation = this.$store.getters.getUserLocation;
      if (!initialLocation) initialLocation = bangkokCoords;

      this.map = L.map(this.mapID, { attributionControl: false });
      this.map.setView(initialLocation, 17);
      L.tileLayer(tilesURL, {
        // edgeBufferTiles: 1,
        tileSize: 512,
        zoomOffset: -1,
        minZoom: 12,
        maxZoom: 19,
      }).addTo(this.map);
      this.map.invalidateSize();

      var iconOpts = this.isPickup ? pickupIcon : dropoffIcon;
      var iconDragOpts = this.isPickup ? pickupDragIcon : dropoffDragIcon;
      var icon = L.AwesomeMarkers.icon(iconOpts);
      var iconDrag = L.AwesomeMarkers.icon(iconDragOpts);

      this.marker = L.marker(initialLocation, { icon: icon }).addTo(this.map);
      this.marker.normalIcon = icon;
      this.marker.dragIcon = iconDrag;

      // set popup
      var opts = { closeButton: false, autoPan: false };
      var text = this.$t("transport.set-pickup-location");
      if (this.stopType === "dropoff") {
        text = this.$t("transport.set-dropoff-location");
      }
      // var content = `<a onclick="clickConfirmPickup()">${text}</a>`;
      var content = text;
      this.marker.bindPopup(content, opts);
      if (this.isPickup) {
        setTimeout(() => this.marker.openPopup(), 1000);
      }

      this.map.on("move", () => {
        this.marker.setLatLng(this.map.getCenter());
      });

      this.map.on("dragstart", () => {
        this.marker.closePopup();
        this.marker.setIcon(this.marker.dragIcon);
        this.moved = true;
      });

      this.map.on("moveend", () => {
        this.moved = true;
        this.marker.setLatLng(this.map.getCenter()); // needed?
        this.marker.center = this.map.getCenter();
        this.marker.setIcon(this.marker.normalIcon);
        this.marker.openPopup();
        if (!this.isPickup) this.checkDropoffNearby();
      });

      this.drawUserLocation();
    },
    checkDropoffNearby() {
      var userCoords = this.$store.getters.getUserLatLon;
      var distMeters = geo.distance(this.marker.center, userCoords);
      if (distMeters < 300) this.showDropoffNearby = true;
      else this.showDropoffNearby = false;
    },
    drawUserLocation() {
      var coords = this.$store.getters.getUserLatLon;
      var icon = L.divIcon({
        html: `<div class="blue-dot pulse"></div>`,
        popupAnchor: [1, -6],
        className: "none",
      });
      this.userMarker = L.marker(coords, { icon: icon });
      this.userMarker.addTo(this.map);
      var popupOpts = { closeButton: false, autoPan: true };
      this.userMarker.bindPopup(this.$t("ride.your-current-gps"), popupOpts);
    },
    confirmLocation() {
      var errMsg = "Set dropoff location to confirm";
      if (this.disableButton) return (this.error = errMsg);
      var coords = this.marker.getLatLng();
      var loader = this.loadingDots();
      this.$store
        .dispatch("reverseGeocode", coords)
        .then((res) => {
          this.console("reverseGeocode", res);
          this.streetName = res.data.address.road;
          if (this.addressName === "") this.locationInput = this.streetName;
          this.emitLocation(coords);
        })
        .catch((err) => console.error("reverseGeocode error", err))
        .finally(() => loader.hide());
    },
    emitLocation(coords) {
      if (!this.streetName) this.streetName = this.googleStreetName;
      // if (this.stopType === "dropoff" && this.streetName) {
      //   // if type dropoff and streetName exists then skip
      return this.reallyEmit(coords);
      // }
      // else for pickup ask for name/description of place
      var prefix = "";
      var pickupMarker = `<i class="fa fa-map-marker-alt" style="color:blue"></i>`;
      var pickupAt = this.$t("driver.pickup-at");
      var dropoffMarker = `<i class="fa fa-map-marker-alt" style="color:green"></i>`;
      var dropoffAt = this.$t("driver.dropoff-at");
      var showCancel = false; // don't show cancel/skip button unless
      if (this.streetName) {
        showCancel = true; // show cancel only if street name lookup successful
        prefix = `${pickupMarker} ${pickupAt} <strong>${this.streetName}</strong>`;
        if (this.stopType === "dropoff") {
          prefix = `${dropoffMarker} ${dropoffAt} <strong>${this.streetName}</strong>`;
        }
      } else {
        prefix = `<strong>${pickupMarker} ${pickupAt} ...</strong>`;
        if (this.stopType === "dropoff") {
          prefix = `<strong>${dropoffMarker} ${dropoffAt} ...</strong>`;
        }
      }
      prefix += `<br><br>`;
      this.$swal({
        // `Give name of building or description of pickup location to help the driver find you, e.g. <i>7-11 on Soi 49</i>`,
        html: `${prefix}<i style="font-size:0.9em">${this.$t(
          "passenger.give-location-name"
        )}</i>`,
        input: "text",
        inputValue: this.addressName,
        inputPlaceholder: this.$t("transport.building-name"),
        showCancelButton: showCancel, //true,
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("transport.skip"),
        inputValidator: (val) => {
          if (!val || val.length < 3)
            return this.$t("transport.please-enter-description");
        },
      }).then((val) => {
        this.reallyEmit(coords, val.value);
      });
    },
    reallyEmit(coords, addressName) {
      if (!addressName) addressName = this.addressName;
      var locationData = {
        mapID: this.mapID,
        stop_type: this.stopType,
        name: this.parseStopName(addressName),
        lat: coords.lat,
        lon: coords.lng,
      };
      this.$emit("setLocation", locationData); // also closes component
    },
    parseStopName(addressName) {
      if (!addressName) return this.streetName;
      if (!this.streetName) return addressName;
      return `${this.streetName}, ${addressName}`;
    },
  },
};
</script>

<style scoped>
.wrapper {
  position: absolute;
  top: 0;
  height: 100vh;
  width: 100vw;
  /* background-color: #2693da; */
  background-color: whitesmoke;
  z-index: 1102;
}
.map-wrapper {
  height: calc(100vh - 100px);
  width: 100%;
}
.map {
  position: relative;
  height: 100%;
  width: 100%;
  border: 1px solid lightgrey;
}
.input {
  border-radius: 0px;
}
input::-webkit-input-placeholder {
  color: #2693da !important;
  opacity: 0.8;
}
.subtitle {
  text-align: left;
  /* color: white; */
  color: #777;
  font-weight: 450;
  margin-bottom: 0;
  padding: 16px;
}
.delete {
  position: absolute;
  right: 12px;
  top: 12px;
}
.confirm-btn {
  position: relative;
  margin: 10px;
  margin-top: -68px;
  z-index: 1200;
}
.confirm-btn .button {
  box-shadow: 0 0 1px 1px white, 0 0 5px black;
}
.center-btn.button {
  margin: 10px;
  padding: 0 9px;
  pointer-events: auto;
  color: rgb(0, 106, 255);
}
.dropoff-warning {
  /* position: absolute; */
  z-index: 2;
  width: 100vw;
  text-align: center;
  margin-top: 5px;
  font-weight: 500;
}
.error-msg {
  position: absolute;
  margin-top: -60px;
  left: 0;
  width: 100%;
  z-index: 2;
  font-weight: 450;
}
</style>

<style lang="scss">
#ride-set-location-map {
  .leaflet-popup-content {
    padding: 5px 5px;
    margin: 1px 0;
    color: rgb(23, 102, 193);
    font-size: 1.1em;
    // max-width: 130px;
  }
  .blue-dot {
    margin-left: 0px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background-color: #2693da;
    opacity: 0.9;
    border: 1px solid #feeaa4b5;
    animation: pulsate-3 3s ease-out infinite;
  }
  @keyframes pulsate-3 {
    0% {
      box-shadow: 0 0 0 1px cyan;
    }
    50% {
      box-shadow: 0 0 0 6px rgba(255, 217, 0, 0.5);
    }
    100% {
      box-shadow: 0 0 0 1px lightskyblue;
    }
  }
  @-webkit-keyframes pulsate-3 {
    0% {
      box-shadow: 0 0 0 1px cyan;
    }
    50% {
      box-shadow: 0 0 0 6px rgba(255, 217, 0, 0.5);
    }
    100% {
      box-shadow: 0 0 0 1px lightskyblue;
    }
  }
  // .pulse {
  //   animation: shadow-pulse 2s infinite;
  // }
  // @keyframes shadow-pulse {
  //   0% {
  //     box-shadow: 0 0 0 0px yellow;
  //   }
  //   50% {
  //     box-shadow: 0 0 0 2px yellow;
  //   }
  //   100% {
  //     box-shadow: 0 0 0 4px #2692da5f;
  //   }
  // }
}
</style>